import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Progress,
  Typography,
  Tooltip,
  Modal,
  Image,
  message,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
} from 'antd';
import {
  PictureOutlined,
  ZoomInOutlined,
  SyncOutlined,
  ArrowsAltOutlined,
  FileTextOutlined,
  ScissorOutlined,
  ReloadOutlined,
  HighlightOutlined,
  ExpandOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import llmService from '../../services/llmService';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 任务类型映射
const typeMap = {
  'IMAGINE': { color: 'blue', icon: <PictureOutlined />, text: '绘图' },
  'UPSCALE': { color: 'orange', icon: <ZoomInOutlined />, text: '放大' },
  'VIDEO': { color: 'orange', icon: <PictureOutlined />, text: '视频' },
  'EDITS': { color: 'orange', icon: <PictureOutlined />, text: '编辑' },
  'VARIATION': { color: 'purple', icon: <SyncOutlined />, text: '变换' },
  'HIGH_VARIATION': { color: 'purple', icon: <SyncOutlined />, text: '强变换' },
  'LOW_VARIATION': { color: 'purple', icon: <SyncOutlined />, text: '弱变换' },
  'PAN': { color: 'cyan', icon: <ArrowsAltOutlined />, text: '平移' },
  'DESCRIBE': { color: 'gold', icon: <FileTextOutlined />, text: '图生文' },
  'BLEND': { color: 'lime', icon: <ScissorOutlined />, text: '图混合' },
  'SHORTEN': { color: 'pink', icon: <ScissorOutlined />, text: '缩词' },
  'REROLL': { color: 'geekblue', icon: <ReloadOutlined />, text: '重绘' },
  'INPAINT': { color: 'volcano', icon: <HighlightOutlined />, text: '局部重绘' },
  'ZOOM': { color: 'teal', icon: <ExpandOutlined />, text: '变焦' },
  'CUSTOM_ZOOM': { color: 'teal', icon: <ExpandOutlined />, text: '自定义变焦' },
};

// 状态映射
const statusMap = {
  'SUCCESS': { color: 'green', icon: <CheckCircleOutlined />, text: '成功' },
  'NOT_START': { color: 'default', icon: <PauseCircleOutlined />, text: '未启动' },
  'SUBMITTED': { color: 'gold', icon: <ClockCircleOutlined />, text: '队列中' },
  'IN_PROGRESS': { color: 'blue', icon: <LoadingOutlined />, text: '执行中' },
  'FAILURE': { color: 'red', icon: <CloseCircleOutlined />, text: '失败' },
  'MODAL': { color: 'orange', icon: <ClockCircleOutlined />, text: '窗口等待' },
};

const MjLogs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [contentModalVisible, setContentModalVisible] = useState(false);
  const [currentContent, setCurrentContent] = useState('');
  const [filters, setFilters] = useState({});
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, pagination.page_size]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
        ...filters,
      };
      // 添加时间范围参数
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_timestamp = dateRange[0].unix();
        params.end_timestamp = dateRange[1].unix();
      }
      const response = await llmService.getMjLogs(params);
      if (response.success) {
        setLogs(response.data?.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
        }));
      } else {
        message.error(response.error || t('Failed to retrieve logs'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve logs'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({});
    setDateRange(null);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const renderType = (action) => {
    const type = typeMap[action] || { color: 'default', icon: <QuestionCircleOutlined />, text: action || '未知' };
    return <Tag color={type.color} icon={type.icon}>{t(type.text)}</Tag>;
  };

  const renderStatus = (status) => {
    const s = statusMap[status] || { color: 'default', icon: <QuestionCircleOutlined />, text: status || '未知' };
    return <Tag color={s.color} icon={s.icon}>{t(s.text)}</Tag>;
  };

  const renderDuration = (record) => {
    if (!record.submit_time || !record.finish_time) return '-';
    const start = new Date(record.submit_time);
    const finish = new Date(record.finish_time);
    const durationMs = finish - start;
    const durationSec = (durationMs / 1000).toFixed(1);
    const color = durationSec > 60 ? 'red' : 'green';
    return <Tag color={color} icon={<ClockCircleOutlined />}>{t('{{durationSec}} seconds', { durationSec })}</Tag>;
  };

  const renderProgress = (progress, record) => {
    const percent = progress ? parseInt(progress.replace('%', '')) : 0;
    const strokeColor = record.status === 'FAILURE' ? '#faad14' : undefined;
    return (
      <Progress
        percent={percent}
        size="small"
        strokeColor={strokeColor}
        style={{ minWidth: 120 }}
      />
    );
  };

  const openImageModal = (url) => {
    setCurrentImage(url);
    setImageModalVisible(true);
  };

  const openContentModal = (content) => {
    setCurrentContent(content);
    setContentModalVisible(true);
  };

  const columns = [
    {
      title: t('Submitted at'),
      dataIndex: 'submit_time',
      key: 'submit_time',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('Time spent'),
      key: 'duration',
      width: 100,
      render: (_, record) => renderDuration(record),
    },
    {
      title: t('Type'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: renderType,
    },
    {
      title: t('Task ID'),
      dataIndex: 'mj_id',
      key: 'mj_id',
      width: 180,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text copyable={{ text }} style={{ fontSize: 12 }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('Task status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatus,
    },
    {
      title: t('Progress'),
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress, record) => renderProgress(progress, record),
    },
    {
      title: t('Output image'),
      dataIndex: 'image_url',
      key: 'image_url',
      width: 100,
      render: (url) => {
        if (!url) return <Text type="secondary">{t('None')}</Text>;
        return (
          <Button type="link" size="small" onClick={() => openImageModal(url)}>
            {t('View image')}
          </Button>
        );
      },
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 150,
      ellipsis: true,
      render: (text) => {
        if (!text) return <Text type="secondary">{t('None')}</Text>;
        return (
          <Tooltip title={text}>
            <Text
              style={{ cursor: 'pointer', color: '#1890ff' }}
              onClick={() => openContentModal(text)}
            >
              {text.length > 20 ? text.substring(0, 20) + '...' : text}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'PromptEn',
      dataIndex: 'prompt_en',
      key: 'prompt_en',
      width: 150,
      ellipsis: true,
      render: (text) => {
        if (!text) return <Text type="secondary">{t('None')}</Text>;
        return (
          <Tooltip title={text}>
            <Text
              style={{ cursor: 'pointer', color: '#1890ff' }}
              onClick={() => openContentModal(text)}
            >
              {text.length > 20 ? text.substring(0, 20) + '...' : text}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: t('Failure reason'),
      dataIndex: 'fail_reason',
      key: 'fail_reason',
      width: 120,
      fixed: 'right',
      ellipsis: true,
      render: (text) => {
        if (!text) return <Text type="secondary">{t('None')}</Text>;
        return (
          <Tooltip title={text}>
            <Text
              type="danger"
              style={{ cursor: 'pointer' }}
              onClick={() => openContentModal(text)}
            >
              {text.length > 15 ? text.substring(0, 15) + '...' : text}
            </Text>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title={<><PictureOutlined /> {t('Midjourney image logs')}</>}
        extra={
          <Space wrap>
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              value={dateRange}
              onChange={setDateRange}
              placeholder={[t('Start time'), t('End time')]}
              style={{ width: 340 }}
            />
            <Input
              placeholder={t('Task ID')}
              value={filters.mj_id || ''}
              onChange={e => setFilters({ ...filters, mj_id: e.target.value })}
              style={{ width: 150 }}
              allowClear
            />
            <Select
              placeholder={t('Status')}
              value={filters.status}
              onChange={value => setFilters({ ...filters, status: value })}
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value="SUCCESS">{t('Success')}</Select.Option>
              <Select.Option value="IN_PROGRESS">{t('Running')}</Select.Option>
              <Select.Option value="FAILURE">{t('Failed')}</Select.Option>
              <Select.Option value="SUBMITTED">{t('Queued')}</Select.Option>
            </Select>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              {t('Search')}
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleReset}>
              {t('Reset')}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={logs}
          rowKey={(record, index) => `${record.mj_id || record.id || index}`}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('{{total}} total', { total }),
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, page_size: pageSize });
            },
          }}
          scroll={{ x: 1500 }}
          size="small"
        />
      </Card>

      {/* 图片预览弹窗 */}
      <Modal
        title={t('Output image')}
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={800}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <Image
            src={currentImage}
            alt="Result"
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
          />
        </div>
      </Modal>

      {/* 内容详情弹窗 */}
      <Modal
        title={t('Full details')}
        open={contentModalVisible}
        onCancel={() => setContentModalVisible(false)}
        footer={[
          <Button key="copy" onClick={() => {
            navigator.clipboard.writeText(currentContent);
            message.success(t('Copied to clipboard'));
          }}>
            {t('Copy')}
          </Button>,
          <Button key="close" type="primary" onClick={() => setContentModalVisible(false)}>
            {t('Close')}
          </Button>,
        ]}
        width={600}
      >
        <div style={{
          background: '#f5f5f5',
          padding: 16,
          borderRadius: 4,
          maxHeight: 400,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}>
          {currentContent}
        </div>
      </Modal>
    </div>
  );
};

export default MjLogs;
